import React from 'react'

import { Pencil, RefreshCw, Trash2 } from 'lucide-react'
import { DateTime } from 'luxon'

import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Schedule } from '@/core/api/entities/schedule'
import { Event } from '@/core/api/entities/zone/event'
import { Occupation } from '@/core/api/entities/zone/occupation'
import { Zone } from '@/core/api/entities/zone/zone'
import { ZoneConnection } from '@/core/api/entities/zone/zoneConnection'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { usePermission } from '@/core/hooks/usePermission'
import useTranslation from '@/core/i18n/useTranslation'
import ActionDropdownMenu from '@/shared/components/molecules/ActionDropdownMenu'
import { SaveDiscardButtons } from '@/shared/components/molecules/SaveDiscardButtons/SaveDiscardButtons'

import { ConnectionStatusTab } from './tabs/ConnectionStatusTab'
import { InformationTab } from './tabs/InformationTab'
import { PlaybackHistoryTab } from './tabs/PlaybackHistoryTab'
import { ZoneDetailsLayout } from './ZoneDetailsLayout'

type ZoneDetailsTab = 'information' | 'connection-status' | 'playback-history'

interface ZoneDetailsUIProps {
	zone?: Zone
	zoneConnections: ZoneConnection[]
	zoneEvents?: Event[]
	isLoading: boolean
	isUpdatingZone?: boolean
	hasUpdateZoneError?: boolean
	isZoneEventsLoading?: boolean
	isZoneConnectionsLoading?: boolean
	zoneEventsError?: Error | null
	zoneConnectionsError?: Error | null
	hasBoxControls?: boolean
	occupationData?: Occupation[]
	isLoadingOccupation?: boolean
	directPlaylists?: Playlist[]
	directSchedules?: Schedule[]
	isLoadingDirectPlaylists?: boolean
	onDownloadReport: () => void
	onDateRangeChange: (startDate: DateTime, endDate: DateTime) => void
	activeTab: ZoneDetailsTab
	onTabChange: (tab: ZoneDetailsTab) => void
	onUpdateZone: (data: UpdateZoneRequestDTO) => void
	onDraftFlags?: (flags: string[]) => void
	onEditZone: () => void
	onPlaylistClick: (radioId: number) => void
	onCampaignClick: (campaignId: number) => void
	onAddPlaylist: () => void
	onAddSchedule: () => void
	onAddCampaign: () => void
	onRemoveCampaign: (campaignId: number) => void
	onAddGroup: () => void
	onRemoveGroup: (groupId: number) => void
	onRemovePlaylistFromZone?: (playlist: Playlist) => void
	onRemovePlaylistFromGroup?: (playlist: Playlist) => void
	onRemovePlaylistFromBoth?: (playlist: Playlist) => void
	onRemoveScheduleFromZone?: (schedule: Schedule) => void
	onRemoveScheduleFromGroup?: (schedule: Schedule) => void
	onRemoveScheduleFromBoth?: (schedule: Schedule) => void
	onDeleteZone: () => void
	onRefreshZone: () => void
	onChangeAdvertAudioVersion: (advertAudioVersionId: number) => void
	isUpdatingAdvertAudioVersion?: boolean
	hasUnsavedChanges?: boolean
	isSaving?: boolean
	onSaveChanges: () => void
	onDiscardChanges: () => void
	hasAdvertAudioVersionFeature?: boolean
}

export const ZoneDetailsUI: React.FC<ZoneDetailsUIProps> = ({
	zone,
	zoneConnections,
	zoneEvents = [],
	isLoading,
	isUpdatingZone = false,
	hasUpdateZoneError = false,
	isZoneEventsLoading = false,
	isZoneConnectionsLoading = false,
	zoneEventsError = null,
	zoneConnectionsError = null,
	hasBoxControls = false,
	occupationData,
	isLoadingOccupation = false,
	directPlaylists = [],
	directSchedules = [],
	isLoadingDirectPlaylists = false,
	onDownloadReport,
	onDateRangeChange,
	activeTab,
	onTabChange,
	onUpdateZone,
	onDraftFlags,
	onEditZone,
	onPlaylistClick,
	onCampaignClick,
	onAddPlaylist,
	onAddSchedule,
	onAddCampaign,
	onRemoveCampaign,
	onAddGroup,
	onRemoveGroup,
	onRemovePlaylistFromZone,
	onRemovePlaylistFromGroup,
	onRemovePlaylistFromBoth,
	onRemoveScheduleFromZone,
	onRemoveScheduleFromGroup,
	onRemoveScheduleFromBoth,
	onDeleteZone,
	onRefreshZone,
	onChangeAdvertAudioVersion,
	isUpdatingAdvertAudioVersion = false,
	hasUnsavedChanges = false,
	isSaving = false,
	onSaveChanges,
	onDiscardChanges,
	hasAdvertAudioVersionFeature = false,
}) => {
	const { t } = useTranslation('common')
	const { t: tZones } = useTranslation('zones')
	const { features } = usePermission()
	const handleInternalTabChange = (tab: string) =>
		onTabChange(tab as ZoneDetailsTab)

	const { hasAnyPermission } = usePermission()
	const canEditZone =
		hasAnyPermission([
			PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
			PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
			PERMISSIONS.UPDATE_USER_PLAYERS,
		]) && features.zone.edit

	const actionMenuItems = [
		...(features.zone.refresh
			? [
					{
						label: tZones('details.actions.refreshCode'),
						icon: RefreshCw,
						onClick: onRefreshZone,
					},
				]
			: []),
		...(canEditZone
			? [
					{
						label: tZones('location.actions.changeInformation'),
						icon: Pencil,
						onClick: onEditZone,
					},
				]
			: []),
		...(zone && features.zone.delete
			? [
					{
						label: tZones('details.actions.deleteZone'),
						icon: Trash2,
						onClick: onDeleteZone,
						variant: 'destructive' as const,
					},
				]
			: []),
	]

	const titleActions =
		actionMenuItems.length > 0 ? (
			<ActionDropdownMenu items={actionMenuItems} />
		) : undefined

	const headerActions = hasUnsavedChanges ? (
		<div className="flex items-center gap-2 flex-wrap justify-end md:justify-start">
			<SaveDiscardButtons
				isSaving={isSaving}
				onSave={onSaveChanges}
				onDiscard={onDiscardChanges}
			/>
		</div>
	) : undefined

	if (isLoading || !zone) {
		return (
			<ZoneDetailsLayout
				activeTab={activeTab}
				onTabChange={handleInternalTabChange}
				headerActions={headerActions}
			>
				<div className="flex items-center justify-center min-h-[400px]">
					<div className="text-muted-foreground">{t('common.loading')}</div>
				</div>
			</ZoneDetailsLayout>
		)
	}

	const renderTabContent = () => {
		switch (activeTab) {
			case 'information':
				return (
					<InformationTab
						zone={zone}
						connections={zoneConnections}
						hasBoxControls={hasBoxControls}
						isUpdating={isUpdatingZone}
						hasUpdateError={hasUpdateZoneError}
						isConnectionsLoading={isZoneConnectionsLoading}
						occupationData={occupationData}
						isLoadingOccupation={isLoadingOccupation}
						directPlaylists={directPlaylists}
						directSchedules={directSchedules}
						isLoadingDirectPlaylists={isLoadingDirectPlaylists}
						onUpdateZone={onUpdateZone}
						onDraftFlags={onDraftFlags}
						onEditZone={onEditZone}
						onPlaylistClick={onPlaylistClick}
						onCampaignClick={onCampaignClick}
						onAddPlaylist={onAddPlaylist}
						onAddSchedule={onAddSchedule}
						onAddCampaign={onAddCampaign}
						onRemoveCampaign={onRemoveCampaign}
						onAddGroup={onAddGroup}
						onRemoveGroup={onRemoveGroup}
						onRemovePlaylistFromZone={onRemovePlaylistFromZone}
						onRemovePlaylistFromGroup={onRemovePlaylistFromGroup}
						onRemovePlaylistFromBoth={onRemovePlaylistFromBoth}
						onRemoveScheduleFromZone={onRemoveScheduleFromZone}
						onRemoveScheduleFromGroup={onRemoveScheduleFromGroup}
						onRemoveScheduleFromBoth={onRemoveScheduleFromBoth}
						onDeleteZone={onDeleteZone}
						onRefreshZone={onRefreshZone}
						onChangeAdvertAudioVersion={onChangeAdvertAudioVersion}
						isUpdatingAdvertAudioVersion={isUpdatingAdvertAudioVersion}
						hasAdvertAudioVersionFeature={hasAdvertAudioVersionFeature}
					/>
				)
			case 'connection-status':
				return (
					<ConnectionStatusTab
						connections={zoneConnections}
						isLoading={isZoneConnectionsLoading}
						error={zoneConnectionsError}
						onDownloadReport={onDownloadReport}
						onDateRangeChange={onDateRangeChange}
					/>
				)
			case 'playback-history':
				return (
					<PlaybackHistoryTab
						zone={zone}
						events={zoneEvents}
						isLoading={isZoneEventsLoading}
						error={zoneEventsError}
					/>
				)
			default:
				return (
					<InformationTab
						zone={zone}
						connections={zoneConnections}
						hasBoxControls={hasBoxControls}
						isUpdating={isUpdatingZone}
						hasUpdateError={hasUpdateZoneError}
						isConnectionsLoading={isZoneConnectionsLoading}
						occupationData={occupationData}
						isLoadingOccupation={isLoadingOccupation}
						directPlaylists={directPlaylists}
						directSchedules={directSchedules}
						isLoadingDirectPlaylists={isLoadingDirectPlaylists}
						onUpdateZone={onUpdateZone}
						onDraftFlags={onDraftFlags}
						onEditZone={onEditZone}
						onPlaylistClick={onPlaylistClick}
						onCampaignClick={onCampaignClick}
						onAddPlaylist={onAddPlaylist}
						onAddSchedule={onAddSchedule}
						onAddCampaign={onAddCampaign}
						onRemoveCampaign={onRemoveCampaign}
						onAddGroup={onAddGroup}
						onRemoveGroup={onRemoveGroup}
						onRemovePlaylistFromZone={onRemovePlaylistFromZone}
						onRemovePlaylistFromGroup={onRemovePlaylistFromGroup}
						onRemovePlaylistFromBoth={onRemovePlaylistFromBoth}
						onRemoveScheduleFromZone={onRemoveScheduleFromZone}
						onRemoveScheduleFromGroup={onRemoveScheduleFromGroup}
						onRemoveScheduleFromBoth={onRemoveScheduleFromBoth}
						onDeleteZone={onDeleteZone}
						onRefreshZone={onRefreshZone}
						onChangeAdvertAudioVersion={onChangeAdvertAudioVersion}
						isUpdatingAdvertAudioVersion={isUpdatingAdvertAudioVersion}
						hasAdvertAudioVersionFeature={hasAdvertAudioVersionFeature}
					/>
				)
		}
	}

	return (
		<ZoneDetailsLayout
			activeTab={activeTab}
			onTabChange={handleInternalTabChange}
			headerActions={headerActions}
			titleActions={titleActions}
		>
			{renderTabContent()}
		</ZoneDetailsLayout>
	)
}

export default ZoneDetailsUI
